import React from 'react';
import { useUserResources } from '@/hooks/useUserResources';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Monitor, Package, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface UserResourcesTabProps {
  userId: string;
}

export const UserResourcesTab = ({ userId }: UserResourcesTabProps) => {
  const { ativos, licencas, isLoading, isError } = useUserResources(userId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-8 text-destructive">
        <AlertCircle className="h-5 w-5 mr-2" />
        Erro ao carregar recursos
      </div>
    );
  }

  const getStatusBadge = (status: string | null) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      operacional: { label: 'Operacional', variant: 'default' },
      em_manutencao: { label: 'Em Manutenção', variant: 'secondary' },
      inativo: { label: 'Inativo', variant: 'destructive' },
      em_uso: { label: 'Em Uso', variant: 'default' },
      disponivel: { label: 'Disponível', variant: 'secondary' },
      expirada: { label: 'Expirada', variant: 'destructive' },
    };
    const config = statusMap[status || ''] || { label: status || 'N/A', variant: 'outline' as const };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const maskLicenseKey = (key: string | null) => {
    if (!key) return 'N/A';
    if (key.length <= 8) return key;
    return `${key.substring(0, 5)}${'*'.repeat(Math.min(key.length - 5, 10))}`;
  };

  return (
    <div className="space-y-6">
      {/* Ativos */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Monitor className="h-4 w-4" />
            Ativos
            <Badge variant="secondary" className="ml-auto">{ativos.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ativos.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum ativo atribuído a este usuário
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Patrimônio</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ativos.map((ativo) => (
                  <TableRow key={ativo.id}>
                    <TableCell className="font-medium">{ativo.nome}</TableCell>
                    <TableCell>{ativo.modelo || '-'}</TableCell>
                    <TableCell>{ativo.patrimonio || '-'}</TableCell>
                    <TableCell>{getStatusBadge(ativo.status_operacional)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Licenças de Software */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4" />
            Licenças de Software
            <Badge variant="secondary" className="ml-auto">{licencas.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {licencas.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma licença atribuída a este usuário
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Software</TableHead>
                  <TableHead>Versão</TableHead>
                  <TableHead>Chave</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licencas.map((licenca) => (
                  <TableRow key={licenca.id}>
                    <TableCell className="font-medium">
                      {licenca.software?.nome || 'N/A'}
                    </TableCell>
                    <TableCell>{licenca.software?.versao || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {maskLicenseKey(licenca.chave_licenca)}
                    </TableCell>
                    <TableCell>
                      {licenca.data_expiracao 
                        ? format(new Date(licenca.data_expiracao), 'dd/MM/yyyy', { locale: ptBR })
                        : 'Perpétua'
                      }
                    </TableCell>
                    <TableCell>{getStatusBadge(licenca.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
